"use client";
import { useState, useCallback, useMemo, useRef } from "react";
import { Icon } from "@ospweb/sdk-ui";
import { cn } from "utils";
import Translations from "@translate/componentsv1/themes/ThemeSearch";
import { I18nUtil } from "@utils/i18n";
import type { ThemeMarketType } from "@/src/const";
import { EmptyState } from "@/src/components/Empty";
import { useThemeSearch } from "@/src/hooks/useThemeSearch";

const I18n = I18nUtil.createScoped(Translations);

interface ThemeSearchProps {
  /** 占位符文本 */
  placeholder?: string;
  /** 市场类型 */
  market: ThemeMarketType;
  /** 语言代码 */
  locale?: string;
  /** 自定义样式类名 */
  className?: string;
  /** 点击主题项的回调 */
  onClickItem?: (item: { id?: string; slug?: string; theme?: string }) => void;
}

export const ThemeSearch = ({
  placeholder = I18n.t("Search themes..."),
  market,
  className,
  onClickItem,
}: ThemeSearchProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // 使用搜索 hook
  const { results, hasResults, isSearching } = useThemeSearch({
    market,
    searchTerm,
    enabled: true,
  });

  // 处理选择
  const handleSelect = useCallback(
    (theme: { id?: string; slug?: string; theme?: string }) => {
      if (onClickItem) {
        onClickItem(theme);
      }
      setSearchTerm("");
      setOpen(false);
    },
    [onClickItem],
  );

  // 处理输入变化
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchTerm(value);
      // 有输入内容才展开
      setOpen(value.trim().length > 0);
    },
    [],
  );

  // 处理点击外部关闭
  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(e.target as Node)
    ) {
      setOpen(false);
      setSearchTerm("");
    }
  }, []);

  // 监听点击外部事件
  useMemo(() => {
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [open, handleClickOutside]);

  // 决定是否显示下拉框：有搜索词
  const shouldShowDropdown = open && isSearching;

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {/* 搜索输入框 */}
      <div className="relative">
        <Icon
          name="search"
          size={20}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-content_tertiary pointer-events-none z-10"
        />
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          placeholder={placeholder}
          className={cn(
            "w-full h-9 pl-10 pr-3 bg-background_white_overlay5 border border-border_white_transparent10 rounded-2",
            "text-content_primary body_medium_m placeholder:text-content_tertiary",
            "focus:outline-none focus:border-border_white_transparent20",
            "transition-colors",
          )}
        />
      </div>

      {/* 下拉列表 */}
      {shouldShowDropdown && (
        <div
          className={cn(
            "absolute top-[calc(100%+8px)] left-0 right-0 z-50",
            "max-h-[400px] overflow-y-auto",
            "bg-background_solid_tertiary border border-border_white_transparent10 rounded-2 shadow-[0px_12px_24px_rgba(0,0,0,0.45)]",
            "p-2 no-scrollbar",
          )}
        >
          {/* 无搜索结果 */}
          {!hasResults && (
            <EmptyState
              title={I18n.t("No search results")}
              hideDescription
              className="h-[200px]"
            />
          )}

          {/* 搜索结果列表 */}
          <div className="flex flex-col">
            {hasResults &&
              results.map((theme, index) => (
                <div
                  data-openreplay-label="themes · ThemeSearch · div"
                  key={`${theme.id}-${index}`}
                  onClick={() => handleSelect(theme)}
                  className={cn(
                    "w-full px-2 py-2 flex items-center rounded-[6px] text-left",
                    "hover:bg-background_white_overlay10 transition-colors",
                    "body_medium_m text-content_primary cursor-pointer",
                  )}
                >
                  <div className="flex-1 truncate body_medium_m text-content_primary">
                    {theme.theme}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};
